import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import { ConfirmDialog } from '@/components/base/Modal';
import { useToast } from '@/components/base/Toast';
import { notifications } from '@/mocks/dashboard';
import { systemUsers as initialUsers, type SystemUser } from '@/mocks/accessControl';
import UserFilters from '@/pages/dashboard/admin/access-control/components/UserFilters';
import UserTable from '@/pages/dashboard/admin/access-control/components/UserTable';
import CreateUserModal from '@/pages/dashboard/admin/access-control/components/CreateUserModal';
import RolePermissionsPanel from '@/pages/dashboard/admin/access-control/components/RolePermissionsPanel';

type Tab = 'users' | 'permissions';

export default function AccessControlPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<SystemUser[]>(initialUsers);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // ── Stats ──
  const stats = useMemo(() => {
    const active = users.filter((u) => u.status === 'active').length;
    const pending = users.filter((u) => u.status === 'pending').length;
    const inactive = users.filter((u) => u.status === 'inactive').length;
    return { total: users.length, active, pending, inactive };
  }, [users]);

  // ── Filtered users ──
  const filteredUsers = useMemo(() => {
    let result = users;
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.phone.includes(q) ||
          u.role.toLowerCase().includes(q),
      );
    }
    if (roleFilter) {
      result = result.filter((u) => u.role === roleFilter);
    }
    if (statusFilter) {
      result = result.filter((u) => u.status === statusFilter);
    }
    return result;
  }, [users, search, roleFilter, statusFilter]);

  const handleRoleChange = useCallback(
    (user: SystemUser, newRole: string) => {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, role: newRole } : u)));
      toast.success(t('accessControl.roleUpdated'));
    },
    [toast, t],
  );

  const handleToggleStatus = useCallback(
    (user: SystemUser) => {
      if (user.status === 'active') {
        setConfirmDialog({
          open: true,
          title: t('accessControl.confirmDeactivateTitle'),
          message: t('accessControl.confirmDeactivateMessage'),
          onConfirm: () => {
            setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: 'inactive' as const } : u)));
            toast.success(t('accessControl.userDeactivated'));
            setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
        });
      } else if (user.status === 'inactive') {
        setConfirmDialog({
          open: true,
          title: t('accessControl.confirmActivateTitle'),
          message: t('accessControl.confirmActivateMessage'),
          onConfirm: () => {
            setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: 'active' as const } : u)));
            toast.success(t('accessControl.userActivated'));
            setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
        });
      }
    },
    [toast, t],
  );

  const handleResendDetails = useCallback(
    (user: SystemUser) => {
      setConfirmDialog({
        open: true,
        title: t('accessControl.confirmResendTitle'),
        message: t('accessControl.confirmResendMessage', { email: user.email }),
        onConfirm: () => {
          toast.success(t('accessControl.detailsResent', { email: user.email }));
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        },
      });
    },
    [toast, t],
  );

  const handleSendDetails = useCallback(
    (user: SystemUser) => {
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: 'active' as const, lastLogin: '\u2014' } : u)));
      toast.success(t('accessControl.detailsSent', { email: user.email }));
    },
    [toast, t],
  );

  const handleCreateOnly = useCallback(
    (data: { name: string; email: string; phone: string; role: string; password: string }) => {
      const newUser: SystemUser = {
        id: `usr-${Date.now()}`,
        name: data.name,
        email: data.email,
        phone: data.phone,
        role: data.role,
        status: 'pending',
        lastLogin: '\u2014',
        createdAt: new Date().toISOString().split('T')[0],
      };
      setUsers((prev) => [newUser, ...prev]);
      toast.success(t('accessControl.userCreated'));
    },
    [toast, t],
  );

  const handleCreateAndSend = useCallback(
    (data: { name: string; email: string; phone: string; role: string; password: string }) => {
      const newUser: SystemUser = {
        id: `usr-${Date.now()}`,
        name: data.name,
        email: data.email,
        phone: data.phone,
        role: data.role,
        status: 'active',
        lastLogin: '\u2014',
        createdAt: new Date().toISOString().split('T')[0],
      };
      setUsers((prev) => [newUser, ...prev]);
      toast.success(t('accessControl.userCreatedAndSent'));
    },
    [toast, t],
  );

  return (
    <AppShell
      pageTitle={t('accessControl.pageTitle')}
      pageSubtitle={t('accessControl.pageSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), onClick: () => navigate('/dashboard/admin') },
        { label: t('accessControl.accessControlBreadcrumb') },
      ]}
      notifications={notifications}
    >
      {/* ── Tab Switcher ── */}
      <div className="flex items-center gap-1 bg-background-200/60 rounded-full p-1 mb-6 w-fit">
        <button
          onClick={() => setTab('users')}
          className={`px-4 py-2 rounded-full text-sm font-label font-medium transition-all cursor-pointer whitespace-nowrap ${
            tab === 'users'
              ? 'bg-background-50 text-foreground-900 shadow-sm'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-group-line mr-1.5" />
          {t('acl.tabUsers')}
        </button>
        <button
          onClick={() => setTab('permissions')}
          className={`px-4 py-2 rounded-full text-sm font-label font-medium transition-all cursor-pointer whitespace-nowrap ${
            tab === 'permissions'
              ? 'bg-background-50 text-foreground-900 shadow-sm'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-shield-keyhole-line mr-1.5" />
          {t('acl.tabPermissions')}
        </button>
      </div>

      {/* ── Users Tab ── */}
      {tab === 'users' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                <i className="ri-group-line text-lg text-primary-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-700 text-foreground-900">{stats.total}</p>
                <p className="text-xs font-label text-foreground-500">{t('accessControl.totalUsers')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-lg text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-700 text-foreground-900">{stats.active}</p>
                <p className="text-xs font-label text-foreground-500">{t('accessControl.activeUsers')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
                <i className="ri-time-line text-lg text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-700 text-foreground-900">{stats.pending}</p>
                <p className="text-xs font-label text-foreground-500">{t('accessControl.pendingUsers')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 flex-shrink-0">
                <i className="ri-user-unfollow-line text-lg text-foreground-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-700 text-foreground-900">{stats.inactive}</p>
                <p className="text-xs font-label text-foreground-500">{t('accessControl.inactiveUsers')}</p>
              </div>
            </div>
          </div>

          <UserFilters
            search={search}
            onSearchChange={setSearch}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onCreateUser={() => setCreateModalOpen(true)}
          />

          <p className="text-xs text-foreground-500 font-label mb-3">
            {t('common.showingCount')}{' '}
            <span className="font-semibold text-foreground-700">{filteredUsers.length}</span>{' '}
            {t('common.of')}{' '}
            <span className="font-semibold text-foreground-700">{users.length}</span>{' '}
            {filteredUsers.length !== 1 ? t('accessControl.usersCountPlural', { count: filteredUsers.length }) : t('accessControl.usersCount', { count: filteredUsers.length })}
          </p>

          <UserTable
            users={filteredUsers}
            onRoleChange={handleRoleChange}
            onToggleStatus={handleToggleStatus}
            onResendDetails={handleResendDetails}
            onSendDetails={handleSendDetails}
          />
        </>
      )}

      {/* ── Role Permissions Tab ── */}
      {tab === 'permissions' && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-6">
          <RolePermissionsPanel onSaved={() => toast.success(t('acl.permissionsSaved'))} />
        </div>
      )}

      {/* ── Create User Modal ── */}
      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreateOnly={handleCreateOnly}
        onCreateAndSend={handleCreateAndSend}
      />

      {/* ── Confirm Dialog ── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
    </AppShell>
  );
}